/**
 * The three labels every AI artifact MUST carry, per
 * `docs/product/teacher-ai-hub-design-document-v1.2.md` §9.
 *
 *   - "AI Draft"
 *   - "Needs Teacher Review"
 *   - "Not Published to LMS"
 *
 * The constants are exported so component tests can assert on them and so
 * a single grep finds every place an artifact-status badge appears.
 *
 * Forbidden copy ("Final", "Auto-published", "Auto Published") is checked
 * elsewhere via a meta-test that scans the bundle source.
 */

export const AI_DRAFT_LABEL = 'AI Draft';
export const NEEDS_REVIEW_LABEL = 'Needs Teacher Review';
export const NOT_PUBLISHED_LABEL = 'Not Published to LMS';

export type AiDraftStatus = 'draft' | 'needs_review';

export interface AiDraftLabelsProps {
  status: AiDraftStatus;
}

/**
 * Render the AI-draft labels as a row of badges. Always emits the trio
 * — even a fresh draft says "Needs Teacher Review" + "Not Published to
 * LMS" because that is the *invariant* the UI promises, not a per-row
 * status flip.
 */
export function AiDraftLabels({ status }: AiDraftLabelsProps) {
  return (
    <span className="ai-draft-labels" data-testid="ai-draft-labels">
      <span
        className="ai-draft-labels__badge ai-draft-labels__badge--draft"
        data-testid="ai-draft-label-draft"
      >
        {AI_DRAFT_LABEL}
      </span>
      <span
        className={
          status === 'needs_review'
            ? 'ai-draft-labels__badge ai-draft-labels__badge--review'
            : 'ai-draft-labels__badge ai-draft-labels__badge--review ai-draft-labels__badge--muted'
        }
        data-testid="ai-draft-label-review"
      >
        {NEEDS_REVIEW_LABEL}
      </span>
      <span
        className="ai-draft-labels__badge ai-draft-labels__badge--unpublished"
        data-testid="ai-draft-label-unpublished"
      >
        {NOT_PUBLISHED_LABEL}
      </span>
    </span>
  );
}
